import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { cn } from '../../../../../../utils/tailwind';
import { IconZoom } from '../../../../../../components/app/icons/zoom';
import { IconClose } from '../../../../../../components/app/icons/close';
import { PageHeadline } from '../../../../../../components/app/page-headline/page-headline';
import { Loading } from '../../../../../../components/app/loading/loading';
import { useRootStore } from '../../../../../../providers/root-store-provider';

type DashboardTradesChartsItem = {
    title: string;
    chart: React.ReactElement;
    badge?: React.ReactNode;
};
export const DashboardChartsItem: React.FC<DashboardTradesChartsItem> = observer(
    ({ title, badge, chart }: DashboardTradesChartsItem) => {
        const [isFullScreen, setIsFullScreen] = useState(false);
        const theme = useTheme();
        const { statisticStore } = useRootStore();
        const { loading } = statisticStore.ratiosStore;

        const triggerStyle: React.CSSProperties = {
            backgroundColor: theme.palette.color.secondary.opacity.low,
            color: theme.palette.common.textInverse,
            transition: `all ${theme.base.transition}`,
        };

        const toggleFullScreen = () => {
            setIsFullScreen(!isFullScreen);
        };

        useEffect(() => {
            const handleKeyDown = (event: KeyboardEvent) => {
                if (event.key === 'Escape' && setIsFullScreen) {
                    setIsFullScreen(false);
                }
            };

            if (isFullScreen) {
                document.addEventListener('keydown', handleKeyDown);
                document.body.style.overflow = 'hidden';
            } else {
                document.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
            }
        }, [isFullScreen, setIsFullScreen]);

        const renderFullScreenChart = () => (
            <div className="fixed left-0 top-0 z-[9999] h-screen w-screen bg-body p-small">
                <div
                    className={cn(
                        'z-[100] flex cursor-pointer items-center justify-center rounded-full hover:bg-brand-secondary [&_i]:text-text-inverse [&_i]:h-[1.4rem] [&_i]:w-[1.4rem]',
                        isFullScreen
                            ? 'absolute right-small top-small h-[4rem] w-[4rem] [&_i]:h-[1.6rem] [&_i]:w-[1.6rem]'
                            : 'absolute right-0 top-1/2 h-[3rem] w-[3rem] -translate-y-1/2',
                    )}
                    style={triggerStyle}
                    onClick={toggleFullScreen}
                >
                    <IconClose />
                </div>
                <PageHeadline title={title} />

                <Loading active={loading} fullWidth fullHeight>
                    {React.cloneElement(chart, {
                        isFullScreen,
                        includeMinorCategories: isFullScreen,
                        ...chart.props,
                    })}
                </Loading>
            </div>
        );

        return (
            <>
                <div className="relative mb-medium flex items-center justify-between">
                    <div className="flex items-center gap-[1rem] text-vsmall leading-vsmall font-bold text-brand-secondary uppercase">
                        {title} {badge}
                    </div>
                    <div
                        className="absolute right-0 top-1/2 z-[100] flex h-[3rem] w-[3rem] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full hover:bg-brand-secondary [&_i]:h-[1.4rem] [&_i]:w-[1.4rem] [&_i]:text-text-inverse"
                        style={triggerStyle}
                        onClick={toggleFullScreen}
                    >
                        <IconZoom />
                    </div>
                </div>
                <div className="flex-1">
                    {React.cloneElement(chart, {
                        ...chart.props,
                    })}
                </div>

                {isFullScreen && createPortal(renderFullScreenChart(), document.body)}
            </>
        );
    },
);
